import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Obra, Registo } from '@/types';
import { formatHoras } from '@/lib/format';
import { Icon } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Sheet';

const RAPIDAS = [1, 2, 4, 8];

interface Props {
  open: boolean;
  obras: Obra[];
  mostrarCentroCusto: boolean;
  /** Present when editing an existing line; absent when adding a new one. */
  registo?: Registo | null;
  subtitle: string;
  busy?: boolean;
  onClose: () => void;
  onSave: (input: { obraId: string; horas: number; observacao: string | null }) => void;
  onDelete?: () => void;
}

export function RegistoSheet({
  open,
  obras,
  mostrarCentroCusto,
  registo,
  subtitle,
  busy,
  onClose,
  onSave,
  onDelete,
}: Props) {
  const { t } = useTranslation();
  const [obraId, setObraId] = useState<string | null>(null);
  const [horas, setHoras] = useState(2);
  const [observacao, setObservacao] = useState('');

  // Reset the form each time the sheet opens, so a previous edit never leaks in.
  useEffect(() => {
    if (!open) return;
    setObraId(registo?.obraId ?? null);
    setHoras(registo?.horas ?? 2);
    setObservacao(registo?.observacao ?? '');
  }, [open, registo]);

  const editar = Boolean(registo);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={editar ? t('sheet.alterarRegisto') : t('sheet.horasEObra')}
      subtitle={subtitle}
    >
      <div className="label-eyebrow">{t('sheet.obra')}</div>
      <div className="mt-2 flex max-h-[168px] flex-col gap-1.5 overflow-y-auto p-px">
        {obras.map((obra) => {
          const ativo = obraId === obra.id;
          return (
            <button
              key={obra.id}
              type="button"
              onClick={() => setObraId(obra.id)}
              className={`flex w-full items-center justify-between gap-2.5 rounded-xl border px-3.5 py-3 text-left transition ${
                ativo ? 'border-conduril bg-[#f2f4fb]' : 'border-conduril-border bg-white'
              }`}
            >
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-[13px] font-semibold text-conduril-ink">
                  {obra.nome}
                </span>
                {mostrarCentroCusto && obra.centroCusto && (
                  <span className="num text-[10px] tracking-wide text-conduril-muted">
                    {obra.centroCusto}
                  </span>
                )}
              </span>
              <span
                className={`h-4 w-4 shrink-0 rounded-full border-2 ${
                  ativo ? 'border-conduril bg-conduril' : 'border-[#ccd1dd] bg-white'
                }`}
              />
            </button>
          );
        })}
      </div>

      <div className="label-eyebrow mt-5">{t('sheet.horas')}</div>
      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          aria-label="minus"
          onClick={() => setHoras((h) => Math.max(0.5, +(h - 0.5).toFixed(2)))}
          className="grid h-[46px] w-[46px] place-items-center rounded-xl border border-[#dfe2ec] bg-white text-conduril"
        >
          <Icon name="remove" className="text-[20px]" />
        </button>
        <div className="num flex-1 text-center text-[30px] font-semibold text-conduril">
          {formatHoras(horas)}
        </div>
        <button
          type="button"
          aria-label="plus"
          onClick={() => setHoras((h) => Math.min(24, +(h + 0.5).toFixed(2)))}
          className="grid h-[46px] w-[46px] place-items-center rounded-xl border border-[#dfe2ec] bg-white text-conduril"
        >
          <Icon name="add" className="text-[20px]" />
        </button>
      </div>
      <div className="mt-2.5 flex gap-1.5">
        {RAPIDAS.map((h) => (
          <button
            key={h}
            type="button"
            onClick={() => setHoras(h)}
            className={`num flex-1 rounded-xl border py-2.5 text-[12px] font-semibold transition ${
              horas === h
                ? 'border-conduril bg-[#f2f4fb] text-conduril'
                : 'border-conduril-border bg-white text-[#5c6373]'
            }`}
          >
            {h}h
          </button>
        ))}
      </div>

      <div className="label-eyebrow mt-5">{t('sheet.observacao')}</div>
      <textarea
        rows={2}
        className="field mt-2 resize-none bg-[#fbfbfd] text-[13px] leading-relaxed"
        placeholder={t('sheet.obsPlaceholder')}
        value={observacao}
        maxLength={500}
        onChange={(e) => setObservacao(e.target.value)}
      />

      <div className="mt-5 flex gap-2">
        {editar && onDelete && (
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            className="shrink-0 rounded-2xl border border-danger-border bg-white px-4 py-3.5 text-[13px] font-semibold text-danger-text"
          >
            {t('common.delete')}
          </button>
        )}
        <button
          type="button"
          disabled={!obraId || busy}
          onClick={() => obraId && onSave({ obraId, horas, observacao: observacao.trim() || null })}
          className="btn-primary flex-1"
        >
          {editar ? t('sheet.guardarAlteracao') : t('sheet.adicionarAoDia')}
        </button>
      </div>
    </Sheet>
  );
}
